import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Row,
  Col,
  Table,
  Button,
  Spinner,
  Modal,
  ModalBody
} from 'reactstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import moment from 'moment';
// import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import webservice from '../../../Service/webservice';
import { AesDecrypt, AesEncrypt, keyPromise } from '../../../Service/crypto';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../../../api';

interface LogEntry {
  id: string;
  ClientName: string;
  Patientname: string;
  InsuranceName?: string;
  UploadedBy?: string;
  Data_Fetched: string;
  uploaded_date: string;
  error_message?: string;
}

interface Option {
  label: string;
  value: string;
}

interface VendorOption {
  label: string;
  value: string;
  key: string;
}

interface VendorLogApiResponse {
  results: LogEntry[];
  totals: { completed: string; inProgress: string; errors: string; total: string };
}

const PAGE_LIMIT = 50;

const VendorLogs: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filtered, setFiltered] = useState<LogEntry[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [clientOptions, setClientOptions] = useState<Option[]>([]);
  const [insuranceOptions, setInsuranceOptions] = useState<Option[]>([]);
  const [uploaderOptions, setUploaderOptions] = useState<Option[]>([]);
  const [vendorOptions, setVendorOptions] = useState<VendorOption[]>([]);
  const [vendorId, setVendorId] = useState<string>('');

  const [selectedClient, setSelectedClient] = useState<Option | null>(null);
  const [selectedInsurance, setSelectedInsurance] = useState<Option | null>(null);
  const [selectedUploader, setSelectedUploader] = useState<Option | null>(null);
  const [dateRange, setDateRange] = useState<string>('');
  const [customStart, setCustomStart] = useState<Date | null>(null);
  const [customEnd, setCustomEnd] = useState<Date | null>(null);

  const [counts, setCounts] = useState<{ completed: string; inProgress: string; errors: string; total: string }>({
    completed: '0',
    inProgress: '0',
    errors: '0',
    total: '0'
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<any>(null);

  const scrollRef = useRef<HTMLDivElement>(null);

  const getDateBounds = () => {
    if (!dateRange) return {};
    let start: moment.Moment, end: moment.Moment;
    switch (dateRange) {
      case 'Today':
        start = moment().startOf('day');
        end = moment().endOf('day');
        break;
      case 'This Week':
        start = moment().startOf('week');
        end = moment().endOf('week');
        break;
      case 'This Month':
        start = moment().startOf('month');
        end = moment().endOf('month');
        break;
      case 'Last Month':
        start = moment().subtract(1, 'month').startOf('month');
        end = moment().subtract(1, 'month').endOf('month');
        break;
      case 'Custom':
        if (customStart && customEnd) {
          start = moment(customStart).startOf('day');
          end = moment(customEnd).endOf('day');
        } else {
          return {};
        }
        break;
      default:
        return {};
    }
    return {
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD')
    };
  };

  const fetchLogs = useCallback(
    async (reset = false) => {
      if (loading) return;
      setLoading(true);

      const { startDate, endDate } = getDateBounds();
      const key = await keyPromise
      const EncParams = await AesEncrypt(key, vendorId || '');
      const body: any = { EncParams, page: reset ? 1 : page, limit: PAGE_LIMIT };
      if (startDate && endDate) Object.assign(body, { startDate, endDate });

      const res = await api.fetch('api/Dashboard/Vendorlog', {
        method: 'POST',
        body: JSON.stringify(body)
      });
      const raw = await res.json();
      const data1: VendorLogApiResponse = await AesDecrypt(key, raw);

      
      const data = data1.results || [];

      
      setCounts(data1.totals);

      const merged = reset ? data : [...logs, ...data];
      setLogs(merged);
      setFiltered(merged);
      setPage(reset ? 2 : page + 1);
      setHasMore(data.length === PAGE_LIMIT);
      setLoading(false);
    },
    [page, logs, loading, vendorId, dateRange, customStart, customEnd]
  );

  useEffect(() => {
    setLogs([]);
    setPage(1);
    setHasMore(true);
    fetchLogs(true);
  }, [vendorId]);

  useEffect(() => {
    (async () => {
      const key = await keyPromise
      const resp = await api.fetch('api/VendorManagement/get_vendor', { method: 'GET' });
      const json = await resp.json();
      const data = await AesDecrypt(key, json);
      const opts = data.map((v: any) => ({ label: v.CompanyName, value: v.CompanyName, key: v._id }));
      setVendorOptions([{ label: 'All', value: '', key: '' }, ...opts]);
    })();
  }, []);

  useEffect(() => {
    const clients = Array.from(new Set(logs.map(l => l.ClientName))).map(v => ({ label: v, value: v }));
    setClientOptions([{ label: 'All', value: '' }, ...clients]);

    const ins = Array.from(new Set(logs.map(l => l.InsuranceName || '')))
      .filter(v => v)
      .map(v => ({ label: v, value: v }));
    setInsuranceOptions([{ label: 'All', value: '' }, ...ins]);

    const ups = Array.from(new Set(logs.map(l => l.UploadedBy || '')))
      .filter(v => v)
      .map(v => ({ label: v, value: v }));
    setUploaderOptions([{ label: 'All', value: '' }, ...ups]);
  }, [logs]);

  useEffect(() => {
    let temp = [...logs];
    if (selectedClient?.value) temp = temp.filter(l => l.ClientName === selectedClient.value);
    if (selectedInsurance?.value) temp = temp.filter(l => l.InsuranceName === selectedInsurance.value);
    if (selectedUploader?.value) temp = temp.filter(l => l.UploadedBy === selectedUploader.value);

    // Client-side date filtering (just to update filtered array quickly)
    if (dateRange && dateRange !== 'Custom') {
      const now = moment();
      let start = now.clone(),
        end = now.clone();
      switch (dateRange) {
        case 'Today':
          start.startOf('day');
          end.endOf('day');
          break;
        case 'This Week':
          start.startOf('week');
          end.endOf('week');
          break;
        case 'This Month':
          start.startOf('month');
          end.endOf('month');
          break;
        case 'Last Month':
          start.subtract(1, 'month').startOf('month');
          end.subtract(1, 'month').endOf('month');
          break;
      }
      temp = temp.filter(l => {
        const d = moment(l.uploaded_date, 'MM/DD/YYYY');
        return d.isBetween(start, end, 'day', '[]');
      });
    }
    if (dateRange === 'Custom' && customStart && customEnd) {
      const s = moment(customStart).startOf('day');
      const e = moment(customEnd).endOf('day');
      temp = temp.filter(l => {
        const d = moment(l.uploaded_date, 'MM/DD/YYYY');
        return d.isBetween(s, e, 'day', '[]');
      });
    }

    setFiltered(temp);
  }, [logs, selectedClient, selectedInsurance, selectedUploader, dateRange, customStart, customEnd]);

  useEffect(() => {
    fetchLogs(true);
  }, []);

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('VendorLogs');

    // Define columns with headers and keys
    worksheet.columns = [
      { header: 'Client Name', key: 'ClientName', width: 20 },
      { header: 'Patient Name', key: 'Patientname', width: 20 },
      { header: 'Insurance Name', key: 'InsuranceName', width: 20 },
      { header: 'Uploaded By', key: 'UploadedBy', width: 20 },
      { header: 'Uploaded Date', key: 'uploaded_date', width: 20 },
      { header: 'Status', key: 'Data_Fetched', width: 15 },
      { header: 'Error Msg', key: 'error_message', width: 30 },
    ];

    // Add rows
    filtered.forEach(l => {
      worksheet.addRow({
        ClientName: l.ClientName,
        Patientname: l.Patientname,
        InsuranceName: l.InsuranceName || '-',
        UploadedBy: l.UploadedBy || '-',
        uploaded_date: l.uploaded_date,
        Data_Fetched: l.Data_Fetched,
        error_message: l.Data_Fetched === 'E' ? l.error_message || '-' : '-',
      });
    });

    // Write to buffer
    const buffer = await workbook.xlsx.writeBuffer();

    // Save using file-saver
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `VendorLogs_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
  };

  const openErrorModal = async (id: string) => {
    setModalOpen(true);
    setModalData(null);
    const key = await keyPromise
    const EncParams = await AesEncrypt(key, id);
    const r = await api.fetch('api/Email-fetchErrorDatas', {
      method: 'POST',
      body: JSON.stringify({ EncParams })
    });
    const j = await r.json();
    setModalData(j[0]);
  };

  const getStatusDetails = (status: string) => {
  const normalized = status.toLowerCase();

  if (normalized === 'c' || normalized === 'completed') {
    return { label: 'Completed', className: 'bg-success-light text-success' };
  } else if (normalized === 'e' || normalized === 'error') {
    return { label: 'Error', className: 'bg-danger-light text-danger' };
  } else if (normalized === 'i' || normalized === 'in progress') {
    return { label: 'In Progress', className: 'bg-warning-light text-warning' };
  } else if (normalized === 'y' || normalized === 'yet to start') {
    return { label: 'Yet to Start', className: 'bg-info-light text-info' };
  } else {
    return { label: '-', className: 'bg-secondary-light text-secondary' };
  }
};

  const badgeClass = (s: string) =>
    s === 'Completed'
      ? 'bg-success-light text-success'
      : s === 'Error'
        ? 'bg-danger-light text-danger'
        : s === 'In progress'
          ? 'bg-warning-light text-warning'
          : 'bg-secondary-light text-secondary';

  const handleVendorChange = (opt: VendorOption | null) => {
    setVendorId(opt?.key || '');
  };

  const handleScroll = () => {
    if (!scrollRef.current || loading || !hasMore) return;
    const { scrollTop, clientHeight, scrollHeight } = scrollRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      fetchLogs();
    }
  };

  return (
    <>
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg" centered>
        <ModalBody>
          {modalData ? (
            <div className="text-center">
              <h5>Error: {modalData.error_message}</h5>
              {modalData.image_base64 && (
                <img
                  src={`data:image/jpeg;base64,${modalData.image_base64}`}
                  alt="Error"
                  style={{ maxWidth: '100%', marginTop: '1rem' }}
                />
              )}
            </div>
          ) : (
            <Spinner />
          )}
        </ModalBody>
      </Modal>

      {/* <Card className="shadow-sm rounded">
        <CardHeader>
          <Row className="align-items-center">
            <Col>
              <h4 className="mb-0">Vendor Logs</h4>
            </Col>
            <Col className="text-end">
              <Button color="primary" onClick={handleExport}>
                Export
              </Button>
            </Col>
          </Row>

          <Row className="mt-3 g-3">
            <Col md={2}>
              <Select
                options={['Today', 'This Week', 'This Month', 'Last Month', 'Custom'].map(v => ({
                  label: v,
                  value: v
                }))}
                isClearable
                placeholder="Date Range"
                onChange={opt => setDateRange(opt?.value || '')}
              />
            </Col>
            {dateRange === 'Custom' && (
              <>
                <Col md={2}>
                  <DatePicker
                    selected={customStart}
                    onChange={setCustomStart}
                    isClearable
                    dateFormat="MM/dd/yyyy"
                    placeholderText="Start Date"
                    className="form-control"
                  />
                </Col>
                <Col md={2}>
                  <DatePicker
                    selected={customEnd}
                    onChange={setCustomEnd}
                    isClearable
                    dateFormat="MM/dd/yyyy"
                    minDate={customStart || new Date()}
                    maxDate={new Date()}
                    placeholderText="End Date"
                    className="form-control"
                  />
                </Col>
              </>
            )}
            <Col md={2}>
              <Select
                options={clientOptions}
                isClearable
                placeholder="Client"
                onChange={setSelectedClient}
              />
            </Col>
            <Col md={2}>
              <Select
                options={insuranceOptions}
                isClearable
                placeholder="Insurance"
                onChange={setSelectedInsurance}
              />
            </Col>
            <Col md={2}>
              <Select
                options={uploaderOptions}
                isClearable
                placeholder="Uploader"
                onChange={setSelectedUploader}
              />
            </Col>
            <Col md={2}>
              <Select
                name="Vendor"
                options={vendorOptions}
                isClearable
                isSearchable
                onChange={handleVendorChange}
                placeholder="Select Vendor"
              />
            </Col>
          </Row>
        </CardHeader>

        <CardBody>
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            style={{ maxHeight: '60vh', overflowY: 'auto' }}
          >
            <Table hover responsive className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>Client</th>
                  <th>Patient</th>
                  <th>Insurance</th>
                  <th>Uploaded By</th>
                  <th>Uploaded Date</th>
                  <th>Status</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((log, i) => {
                  const { label, className } = getStatusDetails(log.Data_Fetched);

                  return (
                    <tr key={i}>
                      <td>{log.ClientName}</td>
                      <td>{log.Patientname}</td>
                      <td>{log.InsuranceName || '-'}</td>
                      <td>{log.UploadedBy || '-'}</td>
                      <td>{log.uploaded_date}</td>
                      <td>
                        <span className={`badge rounded-pill ${className}`}>{label}</span>
                      </td>
                      <td>
                        {label === 'Error' ? (
                          <Button color="link" onClick={() => openErrorModal(log.id)}>
                            View
                          </Button>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  );
                })}
                {loading && (
                  <tr>
                    <td colSpan={7} className="text-center py-3">
                      <Spinner size="sm" />
                    </td>
                  </tr>
                )}
                {!loading && !filtered.length && (
                  <tr>
                    <td colSpan={7} className="text-center py-5">
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </CardBody>

        <CardFooter className="bg-white border-top px-4 py-3">
          <Row className="align-items-center">
            <Col md={8}></Col>
            <Col md={4}>
              <div className="d-flex justify-content-end gap-4">
                <div className="text-center">
                  <div className="text-info fw-bold fs-4">{filtered.length}</div>
                  <div className="text-muted small">Fetched</div>
                </div>
                <div className="text-center">
                  <div className="text-success fw-bold fs-4">{counts.completed}</div>
                  <div className="text-muted small">Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-warning fw-bold fs-4">{counts.inProgress}</div>
                  <div className="text-muted small">In Progress</div>
                </div>
                <div className="text-center">
                  <div className="text-danger fw-bold fs-4">{counts.errors}</div>
                  <div className="text-muted small">Errors</div>
                </div>
                <div className="text-center">
                  <div className="text-dark fw-bold fs-4">{counts.total}</div>
                  <div className="text-muted small">Total</div>
                </div>
              </div>
            </Col>
          </Row>
        </CardFooter>
      </Card> */}
    </>
  );
};

export default VendorLogs;
